import React, { useState, useEffect } from 'react';
import sanctumApi from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import './Admins.css';

const Admins = () => {
  const { user } = useAuth();
  console.log('Current user:', user); // Debug log
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [formError, setFormError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    setLoading(true);
    setError('');
    try {
      const adminsList = await sanctumApi.getAdmins();
      setAdmins(adminsList);
    } catch (error) {
      console.error('Error fetching admins:', error);
      setError('Failed to load administrators');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    
    try {
      if (editingAdmin) {
        await sanctumApi.updateAdmin(editingAdmin._id, formData);
      } else {
        await sanctumApi.createAdmin(formData);
      }
      await fetchAdmins();
      resetForm();
      setSuccessMessage(editingAdmin ? 'Admin updated successfully!' : 'Admin created successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error saving admin:', error);
      setFormError(error.message || 'Failed to save administrator');
    }
  };

  const handleEdit = (admin) => {
    setEditingAdmin(admin);
    setFormData({
      name: admin.name,
      email: admin.email,
      password: ''
    });
    setShowForm(true);
  };

  const handleDelete = (admin) => {
    setDeleteConfirm(admin);
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    
    try {
      await sanctumApi.deleteAdmin(deleteConfirm._id);
      await fetchAdmins();
      setSuccessMessage('Admin deleted successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error deleting admin:', error);
      setError(error.message || 'Failed to delete administrator');
    } finally {
      setDeleteConfirm(null);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', email: '', password: '' });
    setShowForm(false);
    setEditingAdmin(null);
    setFormError('');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="admins-page">
      <div className="page-header">
        <h1>Admin Management</h1>
        <button 
          className="add-btn"
          onClick={() => setShowForm(true)}
        >
          Add New Admin
        </button>
      </div>

      {successMessage && (
        <div className="success-message">{successMessage}</div>
      )}

      {error && <div className="error-message">{error}</div>}

      {deleteConfirm && (
        <div className="form-modal">
          <div className="confirm-dialog">
            <h3>Confirm Delete</h3>
            <p>Are you sure you want to delete <strong>{deleteConfirm.name}</strong>?</p>
            <p className="warning-text">This action cannot be undone.</p>
            <div className="confirm-actions">
              <button className="confirm-btn" onClick={confirmDelete}>
                Yes, Delete
              </button>
              <button className="cancel-btn" onClick={() => setDeleteConfirm(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <div className="form-modal">
          <div className="form-container">
            <h2>{editingAdmin ? 'Edit Admin' : 'Add New Admin'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>Password {editingAdmin && '(leave empty to keep current)'}</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  required={!editingAdmin}
                />
              </div>
              {formError && <div className="form-error">{formError}</div>}
              <div className="form-actions">
                <button type="submit" className="save-btn">
                  {editingAdmin ? 'Update' : 'Create'}
                </button>
                <button type="button" className="cancel-btn" onClick={resetForm}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="admins-table">
        {loading ? (
          <div className="loading">Loading...</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Created Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {admins.length > 0 ? (
                admins.map(admin => (
                  <tr key={admin._id}>
                    <td>{admin.name}</td>
                    <td>{admin.email}</td>
                    <td>{formatDate(admin.createdAt)}</td>
                    <td>
                      <button 
                        className="edit-btn"
                        onClick={() => handleEdit(admin)}
                        disabled={admin._id === (user?._id || user?.id)}
                      >
                        Edit
                      </button>
                      <button 
                        className="delete-btn"
                        onClick={() => handleDelete(admin)}
                        disabled={admin._id === (user?._id || user?.id) || admins.length === 1}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="no-data">
                    No administrators found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default Admins;